import { useEffect, useRef } from 'react';
import { useRecordHeartbeat } from '../api/mutations/useRecording';

/**
 * How often the editor pings the server while a recording is in_progress. The
 * server-side idle cap (its "still recording vs. walked away" boundary) is
 * deliberately independent of this interval.
 */
export const HEARTBEAT_INTERVAL_MS = 20_000;

/**
 * Sends an active-editing heartbeat on a timer while a step recording is
 * in_progress, so the backend can accumulate real recording engagement time.
 * A beat fires only if the user interacted (pointer / key / scroll) within the
 * last interval.
 *
 * @param recordingId  The in-progress recording, or undefined while loading.
 * @param enabled      Mount the timer only while the recording is in_progress.
 */
export function useStepRecordingHeartbeat(recordingId: string | undefined, enabled: boolean): void {
  const { mutate } = useRecordHeartbeat();

  // Epoch ms of the last user interaction. Seeded to mount time; the very first
  // interval won't beat unless the user actually interacts within it.
  const lastInteractionAtRef = useRef<number>(Date.now());

  useEffect(
    function subscribeToInteractionHeartbeat() {
      if (!enabled || !recordingId) return;

      const markInteraction = () => {
        lastInteractionAtRef.current = Date.now();
      };
      const events: Array<keyof WindowEventMap> = [
        'pointerdown',
        'pointermove',
        'keydown',
        'scroll'
      ];
      events.forEach((evt) => window.addEventListener(evt, markInteraction, { passive: true }));

      // Beat only if the user interacted within the last interval. We don't gate
      // on tab focus: a quick detour to another tab still counts, bridged
      // server-side by the idle cap on return. Stopping beats during a sustained
      // idle stretch is what bounds an abandonment to a single clamped gap.
      const intervalId = window.setInterval(() => {
        if (Date.now() - lastInteractionAtRef.current < HEARTBEAT_INTERVAL_MS) {
          mutate(recordingId);
        }
      }, HEARTBEAT_INTERVAL_MS);

      return () => {
        window.clearInterval(intervalId);
        events.forEach((evt) => window.removeEventListener(evt, markInteraction));
      };
    },
    [enabled, recordingId, mutate]
  );
}
